import { useMutation } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  RewriteEventDescriptionCommandInput,
  RewriteEventDescriptionCommandOutput,
  GenerateDescriptionTipsCommandInput,
  GenerateDescriptionTipsCommandOutput,
  GenerateShortDescriptionCommandInput,
  GenerateShortDescriptionCommandOutput,
  SupportedLocale,
} from '@amzn/red-velvet-api';
import { useFeatures, getEnabledFeatures } from '../features';
import { useLocale } from '../locale';
import { toOfferedLocale } from '../../utils/localeUtils';
import { getClient } from '../../utils/getClient';
import { getHttpStatusCode, isAbortError } from '../../utils/errorHelper';

/**
 * A slow model call ends up here, and trying again costs the coordinator another long wait for the
 * same likely answer. Better to show an error and let them press the button again.
 */
const NON_RETRYABLE_STATUSES = [500, 502, 504];

/**
 * The generated client retries a transient failure three times on its own, which multiplies with the
 * retry below and can send a single click to the model up to nine times. One attempt per call leaves
 * `retryTransient` as the only thing deciding whether to try again.
 */
const SINGLE_ATTEMPT = { maxAttempts: 1 };

type Cancellable<TInput> = TInput & { abortSignal?: AbortSignal };

/**
 * The language to ask the model to write the tips in: the one the coordinator has the site in.
 *
 * Only the tips. The rewrite and the short description become the event's own content, so the
 * service reads their language off the description instead and their requests carry no locale at all.
 *
 * Nothing is sent for a locale the API does not offer, which the service answers in English, so a
 * session in a language VC has yet to be translated into still gets feedback. The Localization flag
 * needs no check of its own: with it off the site resolves to en-US, which is English either way.
 */
function useRequestLocale(): SupportedLocale | undefined {
  return toOfferedLocale(useLocale());
}

/**
 * What a caller passes to the tips: everything the operation takes except the language.
 *
 * Leaving `locale` out stops a caller asking for feedback in a language the coordinator is not
 * reading. The compiler is what enforces that; the hook also spreads the caller's input before its
 * own locale, so the hook's wins either way. `abortSignal` is not part of the request: the hook takes
 * it off and hands it to the client.
 */
type TipsRequest = Cancellable<Omit<GenerateDescriptionTipsCommandInput, 'locale'>>;

function retryTransient(failureCount: number, error: Error): boolean {
  if (failureCount >= 2) return false;
  // The coordinator cancelled, so there is nothing left to wait for.
  if (isAbortError(error)) return false;
  const status = getHttpStatusCode(error);
  if (status !== undefined && NON_RETRYABLE_STATUSES.includes(status)) return false;
  return status === undefined || status >= 500;
}

export function useRewriteEventDescription() {
  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const client = getClient(getEnabledFeatures(features, searchParams), SINGLE_ATTEMPT);

  return useMutation<
    RewriteEventDescriptionCommandOutput,
    Error,
    Cancellable<RewriteEventDescriptionCommandInput>
  >({
    mutationKey: ['RedVelvetApi', 'ai', 'rewriteEventDescription'],
    mutationFn: ({ abortSignal, ...input }) =>
      client.rewriteEventDescription(input, { abortSignal }),
    retry: retryTransient,
  });
}

export function useGenerateDescriptionTips() {
  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const client = getClient(getEnabledFeatures(features, searchParams), SINGLE_ATTEMPT);
  const locale = useRequestLocale();

  return useMutation<GenerateDescriptionTipsCommandOutput, Error, TipsRequest>({
    mutationKey: ['RedVelvetApi', 'ai', 'generateDescriptionTips'],
    mutationFn: ({ abortSignal, ...input }) =>
      client.generateDescriptionTips({ ...input, locale }, { abortSignal }),
    retry: retryTransient,
  });
}

export function useGenerateShortDescription() {
  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const client = getClient(getEnabledFeatures(features, searchParams), SINGLE_ATTEMPT);

  return useMutation<
    GenerateShortDescriptionCommandOutput,
    Error,
    Cancellable<GenerateShortDescriptionCommandInput>
  >({
    mutationKey: ['RedVelvetApi', 'ai', 'generateShortDescription'],
    mutationFn: ({ abortSignal, ...input }) =>
      client.generateShortDescription(input, { abortSignal }),
    retry: retryTransient,
  });
}
